import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, type ReactNode } from 'react';

import {
  fetchMyLanguage,
  languageKeys,
  updateMyLanguage,
  type MyLanguage,
} from '@/features/i18n/api/languages';
import { industryVariants } from '@/features/tenants/api/industries';
import { fetchMyBusiness } from '@/features/tenants/api/tenants';
import { I18nProvider } from '@/lib/i18n/I18nProvider';
import { DEFAULT_LANGUAGE, languageMeta } from '@/lib/i18n/languages';
import { availableDictionaryLanguages } from '@/lib/i18n/loader';

const TRANSLATED = new Set(availableDictionaryLanguages());

/**
 * The language to actually render the dashboard in.
 *
 * The server may resolve a language this build ships no `dictionaries/<code>/`
 * for - a store whose admin language was set from the full seeded list, or a
 * preference saved before the language was pulled from the menu. Such a code
 * renders as English anyway, key by key; answering English outright keeps the
 * header's label, `<html lang>` and the number and date formatting agreeing
 * with the words on screen instead of claiming a language nobody is reading.
 */
function renderable(code: string | null | undefined): string {
  if (!code) return DEFAULT_LANGUAGE;
  // `hi-IN` is Hindi spelled as a locale, and the dictionary folder is `hi`.
  const normalised = languageMeta(code).code;
  return TRANSLATED.has(normalised) ? normalised : DEFAULT_LANGUAGE;
}

/**
 * The dashboard's language, as the server resolves it (docs/i18n.md §3, §7):
 * the merchant's own choice, else the store's admin language, else English.
 *
 * A controlled provider over `GET /i18n/me/language`. English while that loads
 * or if it fails, so the dashboard is never blocked on it. Choosing a language
 * shows it at once (the cached answer is updated before the request goes out)
 * and then settles on whatever the server answers - rolling back if the save
 * fails.
 *
 * Owns `<html lang dir>` and the money-formatting locale for the whole layout.
 *
 * Also words the dashboard for the store's industry - a clinic's "Products"
 * reads "Our services" - from the business it already loads for the header
 * (same cache entry). Plain wording until that arrives, or if it fails.
 */
export function AdminI18nProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const mine = useQuery({
    queryKey: languageKeys.mine,
    queryFn: fetchMyLanguage,
    staleTime: 5 * 60_000,
  });

  const business = useQuery({ queryKey: ['tenant', 'me'], queryFn: fetchMyBusiness });
  const industry = business.data?.industry;
  const variants = useMemo(() => industryVariants(industry), [industry]);

  const save = useMutation({
    mutationFn: (code: string) => updateMyLanguage(code),
    onMutate: async (code) => {
      await queryClient.cancelQueries({ queryKey: languageKeys.mine });
      const previous = queryClient.getQueryData<MyLanguage>(languageKeys.mine);
      queryClient.setQueryData<MyLanguage>(languageKeys.mine, {
        preferred: code,
        effective: code,
        store_admin_language: previous?.store_admin_language ?? null,
      });
      return { previous };
    },
    onError: (_error, _code, context) => {
      queryClient.setQueryData(languageKeys.mine, context?.previous);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(languageKeys.mine, data);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: languageKeys.mine }),
  });

  const { mutate } = save;
  const onLanguageChange = useCallback((code: string) => mutate(code), [mutate]);

  return (
    <I18nProvider
      scope="admin"
      applyToDocument
      language={renderable(mine.data?.effective)}
      onLanguageChange={onLanguageChange}
      variants={variants}
    >
      {children}
    </I18nProvider>
  );
}
